import { nanoid } from "nanoid";
import React from "react";

import { Button } from "~/components/button";
import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormFieldReset } from "~/components/form/reset";
import { filter } from "~/tools/filter";
import { tap } from "~/tools/impulse";
import { ImpulseFormShape } from "~/tools/impulse-form";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";

import { OBSERVABILITY_PROMQL_VARIABLES } from "../../routes/dashboards/built-in-variables";
import { DashboardSaveAlert } from "../../routes/dashboards/dashboard/save-alert";
import { useExplorationDrawerState } from "../react/drawer/use-exploration-drawer-state";

import { OBSERVABILITY_PROMQL_VARIABLE_TO_DESCRIPTION } from "./_built-in-variables";
import { BuiltInVariablesTable } from "./_built-in-variables-table";
import { type DrawerState, initVariableState } from "./_drawer-state";
import { FormContextProvider, type FormContextValue } from "./_form-context";
import { FORM_ID } from "./_form-id";
import { VariablesTable } from "./_variables-table";

export type DrawerContentProps = DrawerState;

export const DrawerContent: React.FC<DrawerContentProps> = ({ timeRange, variables, id, onSave }) => {
	const explorationDrawer = useExplorationDrawerState();
	const context = React.useMemo<FormContextValue>(() => ({ timeRange }), [timeRange]);

	return (
		<FormContextProvider value={context}>
			<DashboardSaveAlert className="mb-4" placement="dashboard-variables-drawer" />

			<Form
				id={FORM_ID}
				onSubmit={(output) => {
					onSave(output.map(prop("variable")));
				}}
				state={variables}
			>
				<FormField className="items-stretch" state={variables}>
					<VariablesTable id={id} variables={variables} />

					<div className="mt-4 flex items-end justify-end gap-4">
						<FormFieldReset asChild>
							<Button className="uppercase" variant="secondary">
								Reset variables
							</Button>
						</FormFieldReset>
						<Button
							className="uppercase"
							onClick={() => {
								tap((scope) => {
									explorationDrawer.openCreateDashboardVariable({
										id: `${id}_add_variable`,
										timeRange,
										variables: pipe(
											// pass only valid variables
											variables.getOutput(scope) ?? [],
											map(prop("variable")),
											filter(isPresent),
										),
										onSave: (variable) => {
											variables.setElements((current) => [
												...current,
												ImpulseFormShape({
													id: nanoid(),
													variable: initVariableState(variable),
												}),
											]);
										},
									});
								});
							}}
							variant="primary"
						>
							Add variable
						</Button>
					</div>
				</FormField>
				<div className="mt-4">
					<BuiltInVariablesTable
						title="SUSE Observability PromQL variables"
						variables={OBSERVABILITY_PROMQL_VARIABLES.map((name) => ({
							name,
							description: OBSERVABILITY_PROMQL_VARIABLE_TO_DESCRIPTION[name],
						}))}
					/>
				</div>
			</Form>
		</FormContextProvider>
	);
};
